import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import config from 'config';
import services from '../../../services';
import { Alert, Box, Button, CircularProgress, Collapse } from '@mui/material';
import { ZuoraPaymentMethod, ZuoraSignature } from '@kimuno/api-sdk/lib/services/billing';
import { ZuoraPaymentPageClientSideErrorHandler, ZuoraPaymentPageResponse, ZuoraPaymentPageResponseHandler }
  from '../../../types/ZuoraPayments';
import { isZuoraPaymentObjectDefined, isEveryPayPalPaymentObjectDefined } from '../../../utils/payment';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { sleepUntil } from '../../../utils/sleep';
import { useSelector } from 'react-redux';
import { getSubscriptionUpgradePreview, selectUser } from '../../../reducers/user.selectors';
import PaypalForm from '../../PaypalForm';
import SepaSVG from '../../../assets/sepa.svg';
import PaypalSVG from '../../../assets/paypal.svg';
import CreditCardSVG from '../../../assets/creditCard.svg';
import { borderRadius, customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';

const PAYMENT_METHOD_CREDIT_CARD: ZuoraPaymentMethod = 'creditCard';
const PAYMENT_METHOD_SEPA: ZuoraPaymentMethod = 'sepa';
const PAYMENT_METHOD_PAYPAL: ZuoraPaymentMethod = 'braintree';

type PaymentMethod = {
  id: ZuoraPaymentMethod;
  icon: JSX.Element;
  label: string;
  dataTestId: string;
};

const PAYMENT_METHODS = [
  { id: PAYMENT_METHOD_SEPA, icon: <SepaSVG />, label: 'SEPA', dataTestId: 'sepa-payment-method' },
  { id: PAYMENT_METHOD_PAYPAL, icon: <PaypalSVG />, label: 'PayPal', dataTestId: 'paypal-payment-method' },
  { id: PAYMENT_METHOD_CREDIT_CARD, 
    icon: <CreditCardSVG />, 
    label: 'Credit Card', 
    dataTestId: 'credit-card-payment-method' 
  },
] as PaymentMethod[];
interface SinglePagePaymentMethodsProps {
  onSelect: (paymentMethod: ZuoraPaymentMethod) => void;
  onResponse: ZuoraPaymentPageResponseHandler;
  onClientSideErrorHandler?: ZuoraPaymentPageClientSideErrorHandler;
  selectedMethod?: ZuoraPaymentMethod;
  showPaymentForm: boolean;
  onChange?: (clicked: boolean) => void;  // This prop is passed in by <Controller />
  zuoraHostedPagesSource: string;
  paymentProviderLoadTimeoutMs?: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    value?: ZuoraPaymentMethod;
    index: ZuoraPaymentMethod;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  
  return (
    <Collapse in={value === index} {...other}>
      {value === index && <Box sx={{ paddingTop: spaces.lg }}>{children}</Box>} 
    </Collapse>
  );
};
  
const PaymentMethods: React.FC<SinglePagePaymentMethodsProps> = (props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<ZuoraPaymentMethod | undefined>();
  const [hasCreditCardErrors, setHasCreditCardErrors] = useState<boolean>(false);
  const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
  const user = useSelector(selectUser);
  const [paypalToken, setPaypalToken] = useState('');
  const { paymentProviderLoadTimeoutMs = 12000, selectedMethod } = props;

  const renderPaymentPage = (
    signature: ZuoraSignature,
    paymentMethod: ZuoraPaymentMethod,
    onResponseHandler: ZuoraPaymentPageResponseHandler,
    onClientSideErrorHandler?: ZuoraPaymentPageClientSideErrorHandler,
  ) => {
    const params = {
      locale: 'de_DE',
      style: 'inline',
      submitEnabled: 'true',
      url: config.zuoraHostedPagesUri,
      id: signature.pageId,
      key: signature.key,
      signature: signature.signature,
      token: signature.token,
      tenantId: signature.tenantId,
    };
  
    const onClientSideErrorHandlerProxy = (key: string, code: string, message: string) => {
      // Send the error to the UI
      window.Z.sendErrorMessageToHpm(key, message);
      // Invoke our custom client-side-error-handler
      if (onClientSideErrorHandler) onClientSideErrorHandler(key, code, message);
      setHasCreditCardErrors(true);
    };
  
    window.Z.renderWithErrorHandler(
      params,
      {},
      (response: ZuoraPaymentPageResponse) => onResponseHandler(paymentMethod, response),
      onClientSideErrorHandlerProxy
    );
  };

  useEffect(() => {
    if (selectedMethod) {
      setupPaymentMethod(selectedMethod);
    }
  }, [selectedMethod]);

  const handleSelect = (paymentMethod: ZuoraPaymentMethod) => {
    props.onSelect(paymentMethod);
    props.onChange?.(true);
  };

  const setupPaymentMethod = async (paymentMethod: ZuoraPaymentMethod) => {
    setLoadingError(undefined);
    setPaypalToken('');

    if (paymentMethod === PAYMENT_METHOD_PAYPAL) {
      if (!isEveryPayPalPaymentObjectDefined()) {
        const error = `Not every PayPal payment object is defined.
          !!window.braintree.client: ${!!window.braintree?.client},
          !!window.braintree.paypalCheckout: ${!!window.braintree?.paypalCheckout},
          !!window.paypal.Button: ${!!window.paypal?.Button},`.replace(/(\r\n|\n|\r)/gm, '').replace(/\s{2,}/g, ' ');

        notifyBugsnagHandledError(new Error(error));

        setLoadingError(paymentMethod);
        return;
      }

      services.billing.getBraintreeSignature()
        .then(data => setPaypalToken(data.token))
        .then(() => setIsLoading(false))
        .catch(() => setLoadingError(paymentMethod));
      return;
    }

    setIsLoading(true);
    await sleepUntil(isZuoraPaymentObjectDefined, paymentProviderLoadTimeoutMs)
      .then(() => {
        services.billing.getSignature(paymentMethod, props.zuoraHostedPagesSource)
          .then((signature) =>
            renderPaymentPage(signature, paymentMethod, props.onResponse, props.onClientSideErrorHandler)
          )
          .then(() => setIsLoading(false))
          .catch(() => setLoadingError(paymentMethod));
      })
      .catch(() => {
        if (!isZuoraPaymentObjectDefined()) {
          const error = `Zuora payment object is not defined. !!window.Z: ${!!window.Z}`;
          notifyBugsnagHandledError(new Error(error));
        }

        setLoadingError(paymentMethod);
      });
  };

  const getFilteredPaymentMethods = () => {
    let paymentMethods = PAYMENT_METHODS;
    if ((user?.isBibliothekUser || user?.isCourseUser) && subscriptionUpgradePreview?.paymentMethod) {
      const { paymentMethod } = subscriptionUpgradePreview;
      let method: string | null = paymentMethod;
      switch (subscriptionUpgradePreview.paymentMethod) {
        case 'bankTransfer':
        case 'sepa':
          method = PAYMENT_METHOD_SEPA;
          break;
        case 'creditCard':
          method = PAYMENT_METHOD_CREDIT_CARD;
          break;
        case 'paypal':
          method = PAYMENT_METHOD_PAYPAL;
          break;
        default:
          method = null;
          break;
      }

      if (method) paymentMethods = PAYMENT_METHODS.filter(m => m.id === method);
    }

    return (
      paymentMethods.map(({ id, icon, dataTestId }) => (
        <Button
          data-testid={dataTestId}
          key={id}
          size='large'
          sx={{
            borderRadius: borderRadius.xs,
            borderColor: selectedMethod === id ? palette.primary.main : palette.elevation.outlined,
            '& svg': { width: 'auto', height: 'auto' }
          }}
          variant='outlined'
          onClick={() => handleSelect(id)}
        >
          {icon}
        </Button>
      ))
    );
  };

  const memoizedPaypalForm = useMemo(() => {
    return <PaypalForm token={paypalToken} licenceId={1} onResponse={props.onResponse}/>;
  }, [paypalToken, props.onResponse]);

  const renderPaymentContent = (method: ZuoraPaymentMethod) => {
    switch (method) {
      case PAYMENT_METHOD_SEPA:
        return (
          <Box 
            id='zuora_payment' 
            data-testid='sepa-payment-page'
            sx={{
              minHeight: customSizes.paymentMethods.sepaForm.height,
              '& iframe': {
                with: '100%',
                minHeight: 'inherit',
                height: 'auto'
              },
            }}
          />
        );
      case PAYMENT_METHOD_CREDIT_CARD:
        return (
          <Box 
            id='zuora_payment' 
            data-testid='credit-card-payment-page'
            sx={{
              minHeight: { 
                xs: customSizes.paymentMethods.creditCardForm.height.mobile, 
                lg: customSizes.paymentMethods.creditCardForm.height.desktop 
              },
              '& iframe': {
                with: '100%',
                minHeight: 'inherit',
                ...(!hasCreditCardErrors ? { height: 'auto' } : {})
              }, 
            }}
          />
        );
      case PAYMENT_METHOD_PAYPAL:
        return memoizedPaypalForm;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: spaces.xxs }}>
        {getFilteredPaymentMethods()}
      </Box>
      {loadingError ? (
        <Alert severity='warning' sx={{ marginTop: spaces.lg }}>
          {t('Error while loading payment form, please refresh.')}
        </Alert>
      ) : (
        <>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: spaces.lg }}>
              <CircularProgress
                color='primary'
                sx={{
                  '& .MuiCircularProgress-svg': {
                    height: 'auto',
                    width: 'auto',
                  },
                }}        
              />
            </Box>
          )}
          {PAYMENT_METHODS.map(({ id }) => (
            <CustomTabPanel 
              key={id} 
              value={selectedMethod} 
              index={id}
            >
              {renderPaymentContent(id)}
            </CustomTabPanel>
          ))}
        </>
      )}
    </Box>
  );
};

export default PaymentMethods;
